import React, { memo } from 'react';
import { MapPin, Edit3, ArrowUpRight, Trash2 } from 'lucide-react';
import { getExpiryStatus, formatExpiryDate } from '../../utils/date.js';
import { formatBoxContentsSummary } from '../../utils/box-contents.js';
import { sortLotsByExpiry } from '../../utils/sorting.js';

export const InventoryRow = memo(function InventoryRow({
  group,
  isSelected,
  onToggleSelect,
  onEditItem,
  onBorrowItem,
  onDeleteProduct,
  onViewDetails,
  style
}) {
  const sortedLots = sortLotsByExpiry(group.lots || []);
  const firstItem = sortedLots[0] || group.lots[0] || {};

  const uniqueLocations = Array.from(
    new Set((group.lots || []).map(l => (l.location || '').trim()).filter(Boolean))
  );
  const locationText = uniqueLocations.join(', ');

  return (
    <div
      style={style}
      className={`flex items-center border-b border-slate-100 hover:bg-slate-50/80 transition-colors px-2 sm:px-3 py-2.5 text-xs sm:text-sm select-none ${
        isSelected ? 'bg-indigo-50/50' : 'bg-white'
      }`}
    >
      {/* 1. Selection Checkbox */}
      <div className="w-9 sm:w-10 flex-shrink-0 flex items-center justify-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(group.key)}
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          aria-label={`เลือก ${group.name}`}
        />
      </div>

      {/* 2. Product Name & Meta */}
      <div className="flex-1 min-w-[140px] pr-2 sm:pr-3">
        <button
          onClick={() => onViewDetails(firstItem.id)}
          className="text-left font-bold text-slate-950 hover:text-indigo-600 transition-colors line-clamp-1 cursor-pointer"
        >
          {group.name}
        </button>
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[11px] text-slate-700 font-medium">
          {locationText && (
            <span className="inline-flex items-center gap-0.5 text-cyan-800 font-semibold bg-cyan-100/70 border border-cyan-200/80 px-1.5 py-0.5 rounded">
              <MapPin className="w-3 h-3 text-cyan-700" />
              <span>{locationText}</span>
            </span>
          )}
          {group.brand && <span className="md:hidden text-slate-700 font-medium"><span className="text-slate-500 font-normal">แบรนด์:</span> {group.brand}</span>}
          {group.size && <span className="lg:hidden text-slate-700 font-medium"><span className="text-slate-500 font-normal">ไซส์:</span> {group.size}</span>}
        </div>
      </div>

      {/* 3. Brand (Desktop/Tablet) */}
      <div className="hidden md:block w-20 lg:w-28 flex-shrink-0 text-slate-700 font-medium truncate pr-2">
        {group.brand || '-'}
      </div>

      {/* 4. Size (Desktop) */}
      <div className="hidden lg:block w-20 lg:w-24 flex-shrink-0 text-slate-700 font-medium truncate pr-2">
        {group.size || '-'}
      </div>

      {/* 5. Quantity & Unit */}
      <div className="w-20 sm:w-24 lg:w-28 flex-shrink-0 text-right pr-2 sm:pr-3 font-extrabold text-indigo-700 tabular-nums text-sm sm:text-base">
        {group.totalQuantity}
        <span className="text-xs font-semibold text-slate-600 ml-1">
          {group.unit || 'ชิ้น'}
        </span>
      </div>

      {/* 6. Lots Badges */}
      <div className="hidden sm:flex flex-col gap-1 w-44 md:w-52 lg:w-64 flex-shrink-0 pr-2 sm:pr-3">
        {sortedLots.map((lot) => {
          const status = getExpiryStatus(lot.expiryDate);
          const expText = formatExpiryDate(lot.expiryDate);
          const boxSummary = formatBoxContentsSummary(lot.boxContents);

          const statusBadgeColor =
            status === 'expired'
              ? 'bg-red-50 text-red-800 border-red-200'
              : status === 'expiring-soon'
              ? 'bg-amber-50 text-amber-900 border-amber-200'
              : 'bg-slate-50 text-slate-800 border-slate-200';

          return (
            <div
              key={lot.id}
              className={`flex items-center justify-between gap-1 px-2 py-0.5 rounded text-[11px] border font-semibold ${statusBadgeColor}`}
            >
              <button
                onClick={() => onViewDetails(lot.id)}
                className="hover:underline font-bold truncate cursor-pointer text-left"
              >
                {lot.lot || 'ล็อตหลัก'}: <strong className="font-extrabold">{lot.quantity}</strong> {lot.unit || ''}
              </button>
              <div className="flex items-center gap-1 flex-shrink-0 text-[10px] text-slate-700 font-medium">
                {expText && <span>{expText}</span>}
                {boxSummary && (
                  <span className="text-indigo-700 font-bold truncate max-w-[80px]">
                    ({boxSummary})
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 7. Action Buttons */}
      <div className="w-28 sm:w-32 lg:w-36 flex-shrink-0 flex items-center justify-end gap-1 pr-1 sm:pr-2">
        <button
          onClick={() => onEditItem(firstItem.id)}
          title="แก้ไขสินค้า"
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
        >
          <Edit3 className="w-4 h-4" />
        </button>

        <button
          onClick={() => onBorrowItem(group.key)}
          title="ยืมพัสดุ"
          className="flex items-center gap-0.5 px-2 sm:px-2.5 py-1 rounded-lg bg-amber-400 hover:bg-amber-500 text-slate-900 font-bold text-xs shadow-xs transition-colors cursor-pointer"
        >
          <ArrowUpRight className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">ยืม</span>
        </button>

        <button
          onClick={() => onDeleteProduct(group.key, group.name)}
          title="ลบสินค้า"
          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});
